import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class TimeoutService implements OnDestroy {
  private timeoutPeriod =  900000;
  private timeoutTimer: any;
  private isMonitoring = false;
  
  // Subject to manage event listeners
  private readonly destroy$ = new Subject<void>();
  
  constructor(
    private readonly authService: MsalService,
    private readonly ngZone: NgZone
  ) {}
  
  /**
   * Starts the inactivity monitoring
   * @param customTimeout Optional custom timeout in milliseconds
   */
  startMonitoring(customTimeout?: number): void {
    if (this.isMonitoring) {
      return;
    }
    
    if (customTimeout && customTimeout > 0) {
      this.timeoutPeriod = customTimeout;
    }
    
    this.isMonitoring = true;
    this.addEventListeners();
    this.resetTimer();
  }
  
  // * Stops the inactivity monitoring
  stopMonitoring(): void {
    if (!this.isMonitoring) {
      return;
    }
    
    this.isMonitoring = false;
    this.removeEventListeners();
    clearTimeout(this.timeoutTimer);
  }
  
  // * Resets the inactivity timer
  resetTimer(): void {
    this.ngZone.runOutsideAngular(() => {
      clearTimeout(this.timeoutTimer);
      
      this.timeoutTimer = setTimeout(() => {
        this.ngZone.run(() => {
          this.onTimeout();
        });
      }, this.timeoutPeriod);
    });
  }
  
  // * Handles user activity events
  private readonly onUserActivity = (): void => {
    if (this.isMonitoring) {
      this.resetTimer();
    }
  }
  
  // * Called when inactivity timeout is reached
  private onTimeout(): void {
      this.logout();
  }
  
  logout(): void {
    this.stopMonitoring();
    this.authService.logoutRedirect();
  }
  
  private addEventListeners(): void {
    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('mousemove', this.onUserActivity);
      window.addEventListener('mousedown', this.onUserActivity);
      window.addEventListener('keypress', this.onUserActivity);
      window.addEventListener('touchmove', this.onUserActivity);
      window.addEventListener('touchstart', this.onUserActivity);
      window.addEventListener('scroll', this.onUserActivity);
    });
  }
  
  private removeEventListeners(): void {
    window.removeEventListener('mousemove', this.onUserActivity);
    window.removeEventListener('mousedown', this.onUserActivity);
    window.removeEventListener('keypress', this.onUserActivity);
    window.removeEventListener('touchmove', this.onUserActivity);
    window.removeEventListener('touchstart', this.onUserActivity);
    window.removeEventListener('scroll', this.onUserActivity);
  }
  
  ngOnDestroy(): void {
    this.stopMonitoring();
    this.destroy$.next();
    this.destroy$.complete();
  }
}